import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import { saveAs } from "file-saver";

import { DealListPageQuery } from "./queries/_build_documents_list";
import { DealPaymentsStatisticQuery } from "./queries/common/stats_deals_payments.query";
import { DealProductsStatisticQuery } from "./queries/common/stats_deals_products.query";
import { DealCommonStatisticQuery } from "./queries/common/stats_deals_sum.query";
import { DealExportAsXLSXQuery } from "./queries/_export_as_xlsx";
import { DealExportAsXLSXForSohQuery } from "./queries/_export_as_xlsx_soh";
import { DealExportNoname1Query } from "./queries/_export_noname1";
import { DealExportNoname2Query } from "./queries/_export_noname2";
import generateFilterForQuery from "./queries/common/_tools";

interface WorkPlanBoardState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  lastMeta: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };

  documents: any[];
  previewDealProducts: any[];

  stats: any;
}

const state: WorkPlanBoardState = {
  loading: false,
  loaded: false,
  fatalError: false,

  lastMeta: {
    page: 0,
    pageSize: 0,
    totalCount: 0,
    totalPages: 0,
  },

  documents: [],
  previewDealProducts: [],

  stats: null,
};

function relatedProductsQuery(dealId: string) {
  return {
    columns: {
      id: "basket_item.id",
      container_quantity: "basket_item.container_quantity",
      container_price: "basket_item.container_price",
      notes: "basket_item.notes",
      created_at: "basket_item.created_at",
      updated_at: "basket_item.updated_at",
      product_container_id: "variant.id",
      product_container_name: "variant.name",
      product_container_price: "variant.price",
      product_id: "product.id",
      product_name: "product.name",
      product_is_sample: "variant.is_sample",
      item_is_sample: "basket_item.is_sample",
    },

    from: "s:deal_product_containers",
    from_as: "basket_item",
    joins: [
      {
        join: "s:product_containers",
        type: "left",
        as: "variant",
        on: {
          $eq: ["basket_item.product_container_id", "variant.id"],
        },
      },
      {
        join: "s:products",
        type: "left",
        as: "product",
        on: {
          $eq: ["variant.product_id", "product.id"],
        },
      },
    ],
    order: [{ $desc: ["basket_item.id"] }],
    where: {
      $eq: [`basket_item.deal_id`, { $: dealId }],
    },
  };
}

export const ITEMS_LIST = "ITEMS_LIST";
export const STATS_FOR_PAYMENTS = "STATS_FOR_PAYMENTS";
export const STATS_FOR_PRODUCTS = "STATS_FOR_PRODUCTS";
export const STATS_COMMON = "STATS_COMMON";
export const LOAD_RELATED_DEAL_QUERY = "LOAD_RELATED_DEAL_QUERY";
export const PL_RELAED_PRODUCTS_LIST = "PL_RELAED_PRODUCTS_LIST";

export const actions: ActionTree<WorkPlanBoardState, RootState> = {
  loadData({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action(DealListPageQuery.build(query)),
      ]).then((result: ServerResult) => {
        commit("SET_ITEMS_LIST", result.getOperation(ITEMS_LIST).getContent());
        commit("LOADED_SUCCESS");

        resolve();
      });
    });
  },

  loadDeal({ commit, getters, dispatch }, row) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");
      commit("SET_SELECTED_DEAL_PRODUCTS", []);

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action(relatedProductsQuery(row.id)),
      ]).then((result: ServerResult) => {
        commit(
          "SET_SELECTED_DEAL_PRODUCTS",
          result.getOperation(ITEMS_LIST).getContent(),
        );
        commit("LOADED_SUCCESS");

        resolve();
      });
    });
  },

  loadStatistic({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    console.log(query);

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q(STATS_FOR_PAYMENTS)
          .raw("documents.list")
          .action(DealPaymentsStatisticQuery.build(query)),
        vm
          .$q(STATS_FOR_PRODUCTS)
          .raw("documents.list")
          .action(DealProductsStatisticQuery.build(query)),
        vm
          .$q(STATS_COMMON)
          .raw("documents.list")
          .action(DealCommonStatisticQuery.build(query)),
      ]).then((result: ServerResult) => {
        const products = result
          .getOperation(STATS_FOR_PRODUCTS)
          .getContent()
          .documents.reduce((prev: any[], productData: any) => {
            for (const container of productData.containers) {
              container.is_utilization = productData.is_utilization;
              prev.push(container);
            }

            return prev;
          }, []);

        const products_grouped: any = {};

        products.map((row: any) => {
          const key: string = row.product_name + "-" + row.container_name;
          const productType =
            row.product_type_root || row.product_type_leaf || row.product_type;
          if (!products_grouped[key]) {
            products_grouped[key] = {
              product_name: row.product_name,
              product_type: productType,
              container_name: row.container_name,
              container_count: 0,
              container_weight: 0,
              container_count_utilization: 0,
              container_weight_utilization: 0,
            };
          }
          if (row.is_utilization) {
            products_grouped[key].container_count_utilization = parseInt(
              row.container_count,
            );
            products_grouped[key].container_weight_utilization = parseFloat(
              row.container_weight,
            );
          } else {
            products_grouped[key].container_count = parseInt(
              row.container_count,
            );
            products_grouped[key].container_weight = parseFloat(
              row.container_weight,
            );
          }
        });

        const statData = {
          common: result.getOperation(STATS_COMMON).getContent().documents[0],
          payments: result.getOperation(STATS_FOR_PAYMENTS).getContent()
            .documents,
          products: Object.values(products_grouped),
        };

        commit("SET_STATS_DATA", statData);
        commit("LOADED_SUCCESS");

        resolve();
      });
    });
  },

  exportDataAsXLSX({ rootState }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(DealExportAsXLSXQuery.build(query)),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  },

  exportDataAsXLSXForSoh({ rootState }, query) {
    const vm = (this as any)._vm;
    const debugSoh =
      process.env.VUE_APP_DEBUG_DEAL_EXPORT === "1" ||
      process.env.NODE_ENV === "development";

    const queryMeta = {
      filterKeys: Object.keys(query || {}).filter(
        (k) => k !== "dealId" && k !== "dealIds",
      ),
      dealIdCount: Array.isArray(query?.dealId)
        ? query.dealId.length
        : query?.dealId
          ? 1
          : 0,
    };

    if (debugSoh) {
      console.debug("[DealList export SOH] start", queryMeta);
    }

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(DealExportAsXLSXForSohQuery.build(query)),
      ])
        .then((result: ServerResult) => {
          const exportOpContent = result.getOperation("EXPORT").getContent();
          const fToken = exportOpContent.token;
          const aToken = (rootState as any).ubic.token;
          const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
          saveAs(fileUrl, "export-soh.xlsx");

          if (debugSoh) {
            console.info("[DealList export SOH] finished", { token: fToken });
          }
          resolve();
        })
        .catch((err: Error) => {
          console.error("[DealList export SOH] failed", {
            queryMeta,
            message: err && err.message,
            stack: err && err.stack,
          });
          reject(err);
        });
    });
  },

  exportDataForManufacturePlan({ rootState }, query) {
    const vm = (this as any)._vm;

    const customQuery = DealListPageQuery.build(query, {
      columns: {
        id: "deal.id",
      },
      column_names: {
        id: "#",
      },
    });

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("reports.manufacture")
          .action(customQuery),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  },

  exportDataForNoname1Export({ rootState }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(DealExportNoname1Query.build(query)),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  },

  export1C({ rootState }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      const filter = generateFilterForQuery(query);

      // Если переданы конкретные ID заявок, фильтруем только по ним
      if (query.dealIds && query.dealIds.length > 0) {
        filter.length = 0;
        filter.push({
          $in: ["deal.id", { $: query.dealIds }],
        });
      }

      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.list")
          .action({
            columns: {
              id: "deal.id",
              inn: "counterparty.inn",
              code_1s: {
                $case: [
                  [
                    {
                      $not: [
                        {
                          $or: [
                            { $null: ["counterparty.code_1s"] },
                            { $eq: ["counterparty.code_1s", { $: "" }] },
                          ],
                        },
                      ],
                    },
                    "counterparty.code_1s",
                  ],
                  [{ $: true }, "client.code_1s"],
                ],
              },
              delivery_point: "point.address",
              delivery_point_comment: "point.comment",
              deal_comments: "deal.comments",
              expected_delivery_date: "deal.expected_delivery_date",
              name: "pt.name",
              client_name: "client.name",
              type_id: "deal.type_id",
              deal_comment: "deal.deal_comment",
              mixcart_id: "mix.mixcart_id",
              products: {
                $array_agg: {
                  $obj: {
                    price: "dpc.container_price",
                    price_per_kg: "pc.price_per_kg",
                    product: "pc.sku_1c",
                    quantity: "dpc.container_quantity",
                    container: "pc.name",
                    weight: "pc.weight",
                  },
                },
              },
            },
            from: "s:deals",
            from_as: "deal",
            page: 1,
            page_size: 10000,
            joins: [
              {
                join: "s:counterparties",
                type: "left",
                as: "counterparty",
                on: {
                  $eq: ["deal.counterparty_id", "counterparty.id"],
                },
              },
              {
                join: "s:delivery_points",
                type: "left",
                as: "point",
                on: {
                  $eq: ["deal.delivery_point_id", "point.id"],
                },
              },
              {
                join: "s:payment_types",
                type: "left",
                as: "pt",
                on: {
                  $eq: ["deal.payment_type_id", "pt.id"],
                },
              },
              {
                join: "s:deal_product_containers",
                type: "left",
                as: "dpc",
                on: {
                  $eq: ["deal.id", "dpc.deal_id"],
                },
              },
              {
                join: "s:product_containers",
                type: "left",
                as: "pc",
                on: {
                  $eq: ["dpc.product_container_id", "pc.id"],
                },
              },
              {
                join: "s:cities",
                type: "left",
                as: "city",
                on: {
                  $eq: ["point.city_id", "city.id"],
                },
              },
              {
                join: "s:statuses",
                type: "left",
                as: "status",
                on: {
                  $eq: ["deal.status_id", "status.id"],
                },
              },
              {
                join: "s:pipelines",
                type: "left",
                as: "pipe",
                on: {
                  $eq: ["status.pipeline_id", "pipe.id"],
                },
              },
              {
                join: "s:clients",
                type: "left",
                as: "client",
                on: {
                  $eq: ["deal.client_id", "client.id"],
                },
              },
              {
                join: "s:mixcart_deals",
                type: "left",
                as: "mix",
                on: {
                  $eq: ["deal.id", "mix.deal_id"],
                },
              },
              {
                join: {
                  columns: {
                    deal_id: "dpc.deal_id",
                    sapmle_count: { $count: "dpc.id" },
                  },
                  from: "s:deal_product_containers",
                  from_as: "dpc",
                  joins: [
                    {
                      join: "s:product_containers",
                      type: "left",
                      as: "pc",
                      on: {
                        $eq: ["pc.id", "dpc.product_container_id"],
                      },
                    },
                  ],
                  groups: ["dpc.deal_id"],
                  where: {
                    $or: [
                      { $eq: ["dpc.is_sample", { $: true }] },
                      { $eq: ["pc.is_sample", { $: true }] },
                    ],
                  },
                },
                type: "left",
                as: "dpc_with_samples",
                on: { $eq: ["dpc_with_samples.deal_id", "deal.id"] },
              },
            ],
            groups: [
              "deal.id",
              "counterparty.inn",
              "counterparty.code_1s",
              "point.address",
              "deal.expected_delivery_date",
              "pt.name",
              "point.comment",
              "client.id",
              "client.name",
              "deal.type_id",
              "deal.deal_comment",
              "point.address",
              "mix.mixcart_id",
            ],
            order: [
              {
                $asc: ["client.name"],
              },
              {
                $asc: ["deal.type_id"],
              },
              {
                $asc: ["point.address"],
              },
            ],
            where: {
              $and: [
                {
                  $or: [
                    { $null: ["status.final"] },
                    { $eq: ["status.final", { $: "success" }] },
                  ],
                },
                ...filter,
              ],
            },
          }),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        resolve(
          exportOpContent.documents
            .map((item: any) => {
              item.id = item.id.split(":")[2];
              item.products = item.products.map((cont: any) => {
                cont.price = parseFloat(cont.price);
                cont.price_per_kg =
                  cont.price /
                  (cont.weight * (cont.price_per_kg ? 1 : cont.quantity));
                cont.quantity = parseFloat(cont.quantity) * cont.weight;
                delete cont.weight;
                return cont;
              });
              item.delivery_point_comment =
                (item.delivery_point_comment
                  ? item.delivery_point_comment + "\n"
                  : "") + (item.deal_comments ? item.deal_comments : "");
              delete item.deal_comments;
              return item;
            })
            .sort(function(a: any, b: any) {
              if (
                a["client_name"] + a["type_id"] + a["delivery_point"] >
                b["client_name"] + b["type_id"] + b["delivery_point"]
              )
                return 1;
              if (
                a["client_name"] + a["type_id"] + a["delivery_point"] <
                b["client_name"] + b["type_id"] + b["delivery_point"]
              )
                return -1;
              return 0;
            })
            .map((item: any) => {
              delete item.client_name;
              delete item.type_id;
              return item;
            }),
        );
      });
    });
  },

  exportDataForNoname2Export({ rootState }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(DealExportNoname2Query.build(query)),
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  },

  exportDataForListExport({ commit, getters, dispatch }, query) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      const action = DealListPageQuery.build(query);
      action.page_size = 10000;
      action.page = 1;

      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action(action),
      ]).then((result: ServerResult) => {
        resolve(result.getOperation(ITEMS_LIST).getContent());
      });
    });
  },

  setStatus({ commit }, documents) {
    commit("START_LOADING");
    const vm = (this as any)._vm;
    const _ids: any = [];
    Promise.all(
      documents.map((document: any) => {
        return new Promise((resolve, reject) => {
          vm.$db([
            vm
              .$q("DEAL_TO_DONE")
              .update(document.id, { status_id: "d:statuses:18" }),
          ]).then((result: ServerResult) => {
            // commit('SET_ITEM_LIST', result.getOperation(PL_RELAED_PRODUCTS_LIST).getContent());
            _ids.push(document.id);
            resolve();
          });
        });
      }),
    ).then(() => {
      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action(DealListPageQuery.build({ dealId: _ids })),
      ]).then((result: ServerResult) => {
        commit(
          "UPDATE_ITEMS_LIST",
          result.getOperation(ITEMS_LIST).getContent(),
        );
        commit("LOADED_SUCCESS");
      });
    });
  },
  setDate({ commit }, { dealsIds, newDate }) {
    commit("START_LOADING");
    const vm = (this as any)._vm;
    const _ids: any = [];
    Promise.all(
      dealsIds.map((document: any) => {
        return new Promise((resolve, reject) => {
          vm.$db([
            vm
              .$q("DEAL_TO_DONE")
              .update(document.id, { expected_delivery_date: newDate }),
          ]).then((result: ServerResult) => {
            // commit('SET_ITEM_LIST', result.getOperation(PL_RELAED_PRODUCTS_LIST).getContent());
            _ids.push(document.id);
            resolve();
          });
        });
      }),
    ).then(() => {
      vm.$db([
        vm
          .$q(ITEMS_LIST)
          .raw("documents.list")
          .action(DealListPageQuery.build({ dealId: _ids })),
      ]).then((result: ServerResult) => {
        commit(
          "UPDATE_ITEMS_LIST",
          result.getOperation(ITEMS_LIST).getContent(),
        );
        commit("LOADED_SUCCESS");
      });
    });
  },

  setExportedTo1c({ commit }, documents) {
    commit("START_LOADING");
    const vm = (this as any)._vm;
    const dealIds = documents.map((doc: any) => doc.id);

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("SET_EXPORTED_1C")
          .raw("deals.set_exported_to_1c")
          .action({ deal_ids: dealIds }),
      ])
        .then((result: ServerResult) => {
          const opResult = result.getOperation("SET_EXPORTED_1C");
          if (!opResult.hasError()) {
            // Обновляем список заявок
            vm.$db([
              vm
                .$q(ITEMS_LIST)
                .raw("documents.list")
                .action(DealListPageQuery.build({ dealId: dealIds })),
            ]).then((result: ServerResult) => {
              commit(
                "UPDATE_ITEMS_LIST",
                result.getOperation(ITEMS_LIST).getContent(),
              );
              commit("LOADED_SUCCESS");
              resolve();
            });
          } else {
            commit("LOADED_ERROR");
            reject();
          }
        })
        .catch(() => {
          commit("LOADED_ERROR");
          reject();
        });
    });
  },

  copyDeal({ rootState }, $document) {
    const vm = (this as any)._vm;

    console.log("document", $document);

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q(LOAD_RELATED_DEAL_QUERY)
          .raw("documents.get")
          .action({ id: $document.id }),

        vm
          .$q(PL_RELAED_PRODUCTS_LIST)
          .raw("documents.list")
          .action(relatedProductsQuery($document.id)),
      ]).then((result: ServerResult) => {
        const dealResult = result
          .getOperation(LOAD_RELATED_DEAL_QUERY)
          .getContent().document;
        const dealBasketResult = result
          .getOperation(PL_RELAED_PRODUCTS_LIST)
          .getContent().documents;
        console.log(
          "dealResult, dealBasketResult",
          dealResult,
          dealBasketResult,
        );
        // const opResult = result.getOperation(UNLINK_EMPLOYEE_OP);
        const allowedItemFields = [
          "deal_id",
          "container_quantity",
          "container_price",
          "product_container_id",
          "notes",
        ];

        vm.$db([
          vm
            .$q("COPY_DEAL")
            .create("deals", {
              client_id: dealResult.client_id,
              counterparty_id: dealResult.counterparty_id,
              delivery_point_id: dealResult.delivery_point_id,
              delivery_warehouse_id: dealResult.delivery_warehouse_id,
              manager_id: dealResult.manager_id,
              name: dealResult.name,
              payment_type_id: dealResult.payment_type_id,
              status_id: "d:statuses:13",
            })
            .withManyRelation(
              "deal_product_containers",
              "deal_id",
              (createRelated: any) => {
                return dealBasketResult.map((item: any, index: number) => {
                  const cleanDocuemnt: any = {
                    container_price: item.container_price,
                    container_quantity: item.container_quantity,
                    product_container_id: item.product_container_id,
                    notes: item.notes,
                  };

                  return createRelated(cleanDocuemnt);
                });
              },
            ),
        ]).then((result: ServerResult) => {
          const opResult = result.getOperation("COPY_DEAL");

          if (!opResult.hasError()) {
            resolve();
          } else {
            reject();
          }
        });
      });
    });
  },
  //   relatedDealQuery(db: DataBaseService, dealId: string) {
  //     return db.getById(LOAD_RELATED_DEAL_QUERY, dealId);
  //   }

  //   copyDeal = ($document) => {
  //     console.log('document', $document);

  //     this.db.query([
  //       this.relatedProductsQuery(this.db, $document.id),
  //       this.relatedDealQuery(this.db, $document.id)
  //     ])
  //     .toPromise()
  //     .then((result: ServerResult) => {
  //       const dealResult = result.getOperation(LOAD_RELATED_DEAL_QUERY).getContent().document;
  //       const dealBasketResult = result.getOperation(PL_RELAED_PRODUCTS_LIST).getContent().documents;
  //       console.log('dealResult, dealBasketResult', dealResult, dealBasketResult)
  //       // const opResult = result.getOperation(UNLINK_EMPLOYEE_OP);
  //       const allowedItemFields = ['deal_id', 'container_quantity', 'container_price', 'product_container_id', 'notes'];

  //       this.db.query([
  //         this.dbq.query('COPY_DEAL').create('deals', {
  //           client_id: dealResult.client_id,
  //           counterparty_id: dealResult.counterparty_id,
  //           delivery_point_id: dealResult.delivery_point_id,
  //           delivery_warehouse_id: dealResult.delivery_warehouse_id,
  //           manager_id: dealResult.manager_id,
  //           name: dealResult.name,
  //           payment_type_id: dealResult.payment_type_id,
  //           status_id: 'd:statuses:13'
  //         }).withManyRelation('deal_product_containers', 'deal_id', (createRelated) => {
  //           return dealBasketResult.map((item, index) => {
  //             const cleanDocuemnt: any = {
  //               container_price: item.container_price,
  //               container_quantity: item.container_quantity,
  //               product_container_id: item.product_container_id,
  //               notes: item.notes,
  //             };

  //             return createRelated(cleanDocuemnt);
  //           })
  //         })
  //       ]).toPromise().then((result: ServerResult) => {
  //         const opResult = result.getOperation('COPY_DEAL');

  //         if(!opResult.hasError()) {
  //           this.table.reloadData()
  //           this._snackBar.open('Заявка успешно скопированна', null, {
  //             duration: 4000,
  //           });
  //         } else {
  //           this._snackBar.open('Во время копирования возникла ошибка. Попробуйте позже', null, {
  //             duration: 4000,
  //           });
  //         }
  //       })
  //     })
  //   }
};

export const mutations: MutationTree<WorkPlanBoardState> = {
  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_ITEMS_LIST(state, data) {
    state.documents = data.documents.map((doc: any) => {
      doc.isNew = doc.status_id === "d:statuses:13";
      return doc;
    });

    state.lastMeta = {
      page: data.meta.page,
      pageSize: data.meta.page_size,
      totalCount: data.meta.total_count,
      totalPages: data.meta.total_pages,
    };
  },

  SET_SELECTED_DEAL_PRODUCTS(state, data) {
    state.previewDealProducts = data.documents;
  },

  UPDATE_ITEMS_LIST(state, data) {
    const _new_docs: any = {};
    data.documents.forEach((doc: any) => {
      _new_docs[doc.id] = doc;
    });
    const docs = state.documents.map((doc: any) => {
      if (_new_docs[doc.id] != undefined) {
        doc = _new_docs[doc.id];
      }
      return doc;
    });
    state.documents = docs;
  },

  SET_STATS_DATA(state, data) {
    state.stats = data;
  },
};

const getters = {
  mixcartRejectCount: (state: WorkPlanBoardState) => {
    return state.documents.filter((item: any) => {
      return (
        item.mixcart_is_reject &&
        item.status_id != "d:statuses:19" &&
        !item.mixcart_manual_processed
      );
    }).length;
  },
};

export const STORE_KEY = "$_deal_list";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
